import { Timer } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/core";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  useGetWellbeingSettings, useUpdateWellbeingSettings, getGetWellbeingSettingsQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { usePomodoro } from "@/contexts/PomodoroContext";
import { formatCountdown } from "@/lib/pomodoro";

function DurationField({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <label className="flex items-center gap-2 text-xs text-foreground">
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <input
        type="number"
        min={1}
        max={240}
        value={value}
        onChange={(e) => onChange(Math.max(1, Math.min(240, Number(e.target.value) || 1)))}
        className="h-7 w-16 shrink-0 rounded-md border border-border bg-background px-2 text-right text-xs text-foreground outline-none"
      />
    </label>
  );
}

// Lives in the header, next to the AI Chat button, rather than tucked into
// the folder nav rail — a running timer is something you want visible no
// matter what you're looking at, not just while the inbox is on screen.
// Self-hides when Pomodoro isn't enabled, so callers can just always mount
// it rather than checking settings themselves.
export function PomodoroButton() {
  const queryClient = useQueryClient();
  const { data: settings } = useGetWellbeingSettings({ query: { queryKey: getGetWellbeingSettingsQueryKey() } });
  const updateSettings = useUpdateWellbeingSettings();
  const { phase, isPaused, remainingMs, sessionsCompleted, start, pause, resume, reset } = usePomodoro();

  if (!settings?.pomodoroEnabled) return null;

  const patch = (updates: Partial<typeof settings>) => {
    queryClient.setQueryData(getGetWellbeingSettingsQueryKey(), { ...settings, ...updates });
    updateSettings.mutate(
      { data: updates },
      { onSuccess: (updated) => queryClient.setQueryData(getGetWellbeingSettingsQueryKey(), updated) },
    );
  };

  const phaseLabel = phase === "idle" ? "Not running" : phase === "work" ? "Working" : phase === "longBreak" ? "Long break" : "Break";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Pomodoro timer"
          title="Pomodoro timer"
          className={cn(
            "relative z-10 flex shrink-0 items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-semibold tabular-nums transition-colors",
            phase === "work"
              ? "border-transparent bg-primary text-primary-foreground shadow-sm hover:brightness-110"
              : phase === "break" || phase === "longBreak"
              ? "border-transparent bg-emerald-600 text-white shadow-sm hover:brightness-110"
              : "border-border font-medium text-foreground hover:bg-secondary"
          )}
        >
          {phase !== "idle" && !isPaused && (
            <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-white" />
          )}
          <Timer className="h-4 w-4 shrink-0" />
          {phase === "idle" ? "Pomodoro" : formatCountdown(remainingMs)}
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="end" className="w-72 p-3">
        <div className="flex items-center justify-between gap-3 rounded-md bg-secondary/30 px-3 py-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold tabular-nums text-foreground">{phase === "idle" ? "—:—" : formatCountdown(remainingMs)}</p>
            <p className="truncate text-[11px] text-muted-foreground">{phaseLabel}{sessionsCompleted > 0 && ` · ${sessionsCompleted} done`}</p>
          </div>
          <div className="flex shrink-0 gap-1.5">
            {phase === "idle" ? (
              <Button size="sm" className="h-7 px-2.5 text-xs" onClick={start}>Start</Button>
            ) : isPaused ? (
              <Button size="sm" className="h-7 px-2.5 text-xs" onClick={resume}>Resume</Button>
            ) : (
              <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs" onClick={pause}>Pause</Button>
            )}
            {phase !== "idle" && (
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={reset}>Reset</Button>
            )}
          </div>
        </div>

        <div className="mt-3 space-y-1.5 border-t border-border/60 pt-3">
          <DurationField label="Work session (min)" value={settings.pomodoroWorkMinutes} onChange={(v) => patch({ pomodoroWorkMinutes: v })} />
          <DurationField label="Short break (min)" value={settings.pomodoroBreakMinutes} onChange={(v) => patch({ pomodoroBreakMinutes: v })} />
          <DurationField label="Long break (min)" value={settings.pomodoroLongBreakMinutes} onChange={(v) => patch({ pomodoroLongBreakMinutes: v })} />
          <DurationField label="Sessions before long break" value={settings.pomodoroSessionsBeforeLongBreak} onChange={(v) => patch({ pomodoroSessionsBeforeLongBreak: v })} />
        </div>
      </PopoverContent>
    </Popover>
  );
}
